/**
 * ZonePage — Zone Home
 *
 * The "niche community" landing for each of Enume's 8 creative zones.
 * Accessible at /zone/:zoneId (e.g. /zone/build, /zone/taste).
 *
 * Three live stat panels (the ones that make this page feel alive):
 *   1. Active Now      — how many people have is_working=true in this zone right now
 *   2. Top Forked Week — the most-forked project in the last 7 days in this zone
 *   3. Zone Affinity   — which zones fork into / out of this zone most (the magic)
 *
 * Plus:
 *   - Recent projects grid with zone-colored accents
 *   - Open challenges count
 *   - "View on Map" deep-link to the main CosmicMap filtered to this zone
 *   - Navigation between zones (chips at top)
 *
 * Place at: src/pages/ZonePage.jsx
 *
 * Route in App.jsx:
 *   <Route path="/zone/:zoneId" element={
 *     <ProtectedRoute><AppLayout><ZonePage /></AppLayout></ProtectedRoute>
 *   } />
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Package, Palette, Music, Utensils, BookOpen, Stethoscope, Cpu, TrendingUp,
  GitFork, Eye, Users, Zap, Target, Map, ArrowRight, ArrowUpRight,
  Sparkles, Radio, Clock, ChevronRight, RefreshCw, ExternalLink,
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';

// ============================================================================
// ZONE CONFIG  (mirrors zones.js — no import to keep this page self-contained)
// ============================================================================

const ZONES = {
  build:     { id: 'build',     label: 'Build Something',      shortLabel: 'Build',    color: '#f59e0b', icon: Package },
  beautiful: { id: 'beautiful', label: 'Make It Beautiful',    shortLabel: 'Beautiful',color: '#ec4899', icon: Palette },
  sound:     { id: 'sound',     label: 'Make It Sound Good',   shortLabel: 'Sound',    color: '#06b6d4', icon: Music },
  taste:     { id: 'taste',     label: 'Make It Taste Good',   shortLabel: 'Taste',    color: '#f97316', icon: Utensils },
  story:     { id: 'story',     label: 'Spin A Tale',          shortLabel: 'Story',    color: '#8b5cf6', icon: BookOpen },
  heal:      { id: 'heal',      label: 'Patch People Up',      shortLabel: 'Heal',     color: '#ef4444', icon: Stethoscope },
  smart:     { id: 'smart',     label: 'Make It Smart',        shortLabel: 'Smart',    color: '#6366f1', icon: Cpu },
  sell:      { id: 'sell',      label: 'Sell It',              shortLabel: 'Sell',     color: '#10b981', icon: TrendingUp },
};

const ZONE_TAGLINES = {
  build:     'Makers, fabricators, and builders who create physical things.',
  beautiful: 'Artists, designers, and photographers who shape how the world looks.',
  sound:     'Musicians, producers, and audio engineers who fill silence.',
  taste:     'Cooks, bakers, and food experimenters who feed the senses.',
  story:     'Writers, filmmakers, and storytellers who shape narrative.',
  heal:      'Healthcare students and wellness creators making people better.',
  smart:     'Coders, engineers, and AI builders who make things think.',
  sell:      'Entrepreneurs, marketers, and brand builders who create value.',
};

// ============================================================================
// HELPERS
// ============================================================================

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const s = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (s < 60)     return 'just now';
  if (s < 3600)   return `${Math.floor(s / 60)}m ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return `${Math.floor(s / 2592000)}mo ago`;
};

const formatNumber = (n) => {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000)    return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

// ============================================================================
// PULSE DOT  (animated green dot for "active now")
// ============================================================================

const PulseDot = ({ color = '#10b981' }) => (
  <span className="relative inline-flex h-2.5 w-2.5 flex-shrink-0">
    <span
      className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
      style={{ backgroundColor: color }}
    />
    <span
      className="relative inline-flex rounded-full h-2.5 w-2.5"
      style={{ backgroundColor: color }}
    />
  </span>
);

// ============================================================================
// STAT CARD — the three hero panels
// ============================================================================

const StatCard = ({ label, children, icon: Icon, color, className }) => (
  <div
    className={cn(
      'relative rounded-2xl border bg-zinc-900/60 backdrop-blur-sm overflow-hidden p-5',
      className,
    )}
    style={{ borderColor: `${color}25` }}
  >
    {/* Subtle color bleed top-left */}
    <div
      className="absolute -top-10 -left-10 w-32 h-32 rounded-full opacity-[0.07] blur-2xl pointer-events-none"
      style={{ backgroundColor: color }}
    />
    <p className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5" style={{ color }} />}
      {label}
    </p>
    {children}
  </div>
);

// ============================================================================
// ACTIVE NOW PANEL
// ============================================================================

const ActiveNowPanel = ({ activeNow, zoneColor, totalProjects }) => (
  <StatCard label="Active Right Now" icon={Radio} color={zoneColor}>
    <div className="flex items-end gap-3">
      <span className="text-5xl font-black text-white tabular-nums leading-none">
        {activeNow}
      </span>
      <div className="pb-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <PulseDot color={zoneColor} />
          <span className="text-xs text-zinc-400">
            {activeNow === 1 ? 'creator' : 'creators'} working now
          </span>
        </div>
        <p className="text-[11px] text-zinc-600">
          out of {formatNumber(totalProjects)} total projects
        </p>
      </div>
    </div>
    {activeNow === 0 && (
      <p className="text-[12px] text-zinc-600 mt-2 italic">
        Be the first — toggle "Go Live" on your project.
      </p>
    )}
  </StatCard>
);

// ============================================================================
// TOP FORKED THIS WEEK PANEL
// ============================================================================

const TopForkedPanel = ({ topFork, zoneColor }) => {
  const navigate = useNavigate();

  if (!topFork) {
    return (
      <StatCard label="Top Forked This Week" icon={GitFork} color={zoneColor}>
        <p className="text-[12px] text-zinc-600 italic mt-1">
          No forks yet this week. Fork something.
        </p>
      </StatCard>
    );
  }

  return (
    <StatCard label="Top Forked This Week" icon={GitFork} color={zoneColor}>
      <button
        onClick={() => navigate(`/dashboard?project=${topFork.id}`)}
        className="w-full text-left group"
      >
        <div className="flex items-center gap-3">
          {/* Thumbnail */}
          <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border border-zinc-800">
            {topFork.image_url ? (
              <img src={topFork.image_url} alt={topFork.name} className="w-full h-full object-cover" />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-xl font-black select-none"
                style={{ background: `linear-gradient(135deg, ${zoneColor}20, #18181b)`, color: zoneColor, opacity: 0.5 }}
              >
                {topFork.name?.[0]?.toUpperCase()}
              </div>
            )}
          </div>
          {/* Info */}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate group-hover:text-purple-300 transition-colors">
              {topFork.name}
            </p>
            <p className="text-[11px] text-zinc-500 truncate mt-0.5">
              by @{topFork.author?.username}
            </p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: zoneColor }}>
                <GitFork className="w-3 h-3" />
                {topFork.fork_count} forks
              </span>
              <span className="flex items-center gap-1 text-[11px] text-zinc-600">
                <Eye className="w-3 h-3" />
                {formatNumber(topFork.view_count)} views
              </span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
        </div>
      </button>
    </StatCard>
  );
};

// ============================================================================
// ZONE AFFINITY PANEL  — the most unique stat on any platform
// ============================================================================

const ZoneAffinityPanel = ({ incomingZones, outgoingZones, currentZoneId, zoneColor }) => {
  const [tab, setTab] = useState('incoming');
  const list = tab === 'incoming' ? incomingZones : outgoingZones;

  return (
    <StatCard label="Zone Connections" icon={Sparkles} color={zoneColor} className="md:col-span-1">
      {/* Tab toggle */}
      <div className="flex gap-1 mb-4 p-0.5 bg-zinc-800/50 rounded-xl w-fit">
        {[
          { id: 'incoming', label: 'Forks in' },
          { id: 'outgoing', label: 'Forks out' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-1.5 rounded-[10px] text-[11px] font-semibold transition-all',
              tab === t.id
                ? 'bg-zinc-700 text-white shadow'
                : 'text-zinc-500 hover:text-zinc-300',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <p className="text-[12px] text-zinc-600 italic">No cross-zone connections yet.</p>
      ) : (
        <div className="space-y-2">
          {list.map(({ zone, label, count }) => {
            const z = ZONES[zone];
            const ZoneIcon = z?.icon;
            const maxCount = list[0]?.count || 1;
            const pct = Math.round((count / maxCount) * 100);

            return (
              <div key={zone} className="group">
                <div className="flex items-center gap-2 mb-1">
                  {ZoneIcon && (
                    <ZoneIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: z.color }} />
                  )}
                  <span className="text-[12px] font-medium text-zinc-300 flex-1">{label}</span>
                  <span className="text-[11px] font-semibold tabular-nums" style={{ color: zoneColor }}>
                    {count}
                  </span>
                </div>
                {/* Bar */}
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${zoneColor}90, ${z?.color || zoneColor}80)`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Insight text */}
      {incomingZones.length > 0 && (
        <p className="text-[11px] text-zinc-600 mt-4 leading-relaxed">
          {tab === 'incoming'
            ? `${ZONES[incomingZones[0]?.zone]?.shortLabel || incomingZones[0]?.zone} projects most often spark something here.`
            : `${ZONES[currentZoneId]?.shortLabel} ideas most often land in ${ZONES[outgoingZones[0]?.zone]?.shortLabel || outgoingZones[0]?.zone}.`
          }
        </p>
      )}
    </StatCard>
  );
};

// ============================================================================
// PROJECT CARD  (compact grid card)
// ============================================================================

const ProjectCard = ({ project, zoneColor, onClick }) => (
  <button
    onClick={onClick}
    className="group text-left rounded-2xl border border-zinc-800/60 bg-zinc-900/40 overflow-hidden
               hover:border-zinc-700/60 transition-all duration-200 active:scale-[0.98]"
  >
    {/* Image */}
    <div className="h-32 overflow-hidden relative">
      {project.image_url ? (
        <img
          src={project.image_url}
          alt={project.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center text-4xl font-black select-none"
          style={{ background: `linear-gradient(135deg, ${zoneColor}18, #18181b)`, color: zoneColor, opacity: 0.15 }}
        >
          {project.name?.[0]?.toUpperCase()}
        </div>
      )}
      {/* Working pulse */}
      {project.is_working && (
        <div className="absolute top-2 right-2">
          <PulseDot color={zoneColor} />
        </div>
      )}
    </div>

    {/* Info */}
    <div className="p-3">
      <p className="text-[13px] font-semibold text-white truncate group-hover:text-purple-300 transition-colors mb-0.5">
        {project.name}
      </p>
      <p className="text-[11px] text-zinc-500 truncate">
        @{project.author?.username}
      </p>
      <div className="flex items-center gap-3 mt-2">
        <span className="flex items-center gap-1 text-[11px] text-zinc-600">
          <GitFork className="w-3 h-3" />
          {project.fork_count}
        </span>
        <span className="flex items-center gap-1 text-[11px] text-zinc-600">
          <Eye className="w-3 h-3" />
          {formatNumber(project.view_count)}
        </span>
        <span className="text-[11px] text-zinc-700 ml-auto">
          {timeAgo(project.created_at)}
        </span>
      </div>
    </div>
  </button>
);

// ============================================================================
// SKELETON LOADER
// ============================================================================

const SkeletonCard = () => (
  <div className="rounded-2xl border border-zinc-800/40 bg-zinc-900/30 overflow-hidden animate-pulse">
    <div className="h-32 bg-zinc-800/50" />
    <div className="p-3 space-y-2">
      <div className="h-3 bg-zinc-800/70 rounded-full w-3/4" />
      <div className="h-2.5 bg-zinc-800/50 rounded-full w-1/2" />
    </div>
  </div>
);

// ============================================================================
// ZONE NAV CHIPS
// ============================================================================

const ZoneChips = ({ currentZoneId }) => {
  const navigate = useNavigate();
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
      {Object.values(ZONES).map(zone => {
        const Icon = zone.icon;
        const isActive = zone.id === currentZoneId;
        return (
          <button
            key={zone.id}
            onClick={() => navigate(`/zone/${zone.id}`)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold flex-shrink-0 transition-all duration-200',
              isActive
                ? 'text-white shadow-lg'
                : 'bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800',
            )}
            style={isActive ? {
              backgroundColor: `${zone.color}22`,
              border: `1px solid ${zone.color}50`,
              color: zone.color,
            } : { border: '1px solid transparent' }}
          >
            <Icon className="w-3.5 h-3.5" />
            {zone.shortLabel}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function ZonePage() {
  const { zoneId } = useParams();
  const navigate = useNavigate();

  const zone = ZONES[zoneId];

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  const fetchStats = useCallback(async () => {
    if (!zone) return;
    try {
      const res = await api.get(`/platform/stats/zones/${zoneId}`);
      setStats(res.data);
      setLastRefreshed(new Date());
      setError(null);
    } catch (err) {
      setError('Could not load zone stats.');
    } finally {
      setLoading(false);
    }
  }, [zoneId, zone]);

  useEffect(() => {
    setLoading(true);
    setStats(null);
    fetchStats();
    // Refresh active-now every 30s
    intervalRef.current = setInterval(fetchStats, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchStats]);

  // Unknown zone
  if (!zone) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400 mb-4">Zone not found.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-purple-400 hover:text-purple-300 text-sm underline"
          >
            Back to map
          </button>
        </div>
      </div>
    );
  }

  const ZoneIcon = zone.icon;
  const color = zone.color;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">

      {/* ── Background ambient glow ──────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.04] blur-3xl"
          style={{ backgroundColor: color }}
        />
        <div
          className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full opacity-[0.03] blur-3xl"
          style={{ backgroundColor: color }}
        />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-6 pb-20">

        {/* ── Zone chips nav ──────────────────────────────────────────── */}
        <div className="mb-8">
          <ZoneChips currentZoneId={zoneId} />
        </div>

        {/* ── Zone header ─────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Zone icon circle */}
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${color}30, ${color}10)`, border: `1px solid ${color}30` }}
              >
                <ZoneIcon className="w-7 h-7" style={{ color }} />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                  {zone.label}
                </h1>
                <p className="text-sm text-zinc-400 mt-0.5 max-w-md">
                  {ZONE_TAGLINES[zoneId]}
                </p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Refresh */}
              <button
                onClick={fetchStats}
                className="p-2 rounded-xl bg-zinc-800/50 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
                title="Refresh stats"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              {/* View on Map */}
              <button
                onClick={() => navigate(`/dashboard?zone=${zoneId}`)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all"
                style={{
                  background: `${color}18`,
                  border: `1px solid ${color}30`,
                  color,
                }}
              >
                <Map className="w-3.5 h-3.5" />
                View on Map
              </button>
            </div>
          </div>

          {/* Project count + open challenges quick stats */}
          {stats && (
            <div className="flex items-center gap-4 mt-4 ml-[4.5rem]">
              <span className="text-sm text-zinc-400">
                <span className="text-white font-bold">{formatNumber(stats.total_projects)}</span> projects
              </span>
              {stats.open_challenges > 0 && (
                <button
                  onClick={() => navigate('/challenges')}
                  className="flex items-center gap-1.5 text-sm"
                >
                  <Target className="w-3.5 h-3.5" style={{ color }} />
                  <span className="font-bold" style={{ color }}>{stats.open_challenges}</span>
                  <span className="text-zinc-500">open challenges</span>
                  <ArrowUpRight className="w-3 h-3 text-zinc-600" />
                </button>
              )}
              {lastRefreshed && (
                <span className="text-[11px] text-zinc-700 ml-auto">
                  Updated {timeAgo(lastRefreshed)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Error state ─────────────────────────────────────────────── */}
        {error && (
          <div className="mb-6 p-4 rounded-2xl border border-red-500/20 bg-red-500/5 text-sm text-red-400">
            {error}
            <button onClick={fetchStats} className="ml-2 underline hover:no-underline">Retry</button>
          </div>
        )}

        {/* ── THREE STAT PANELS ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">

          {/* 1. Active now */}
          {loading ? (
            <div className="rounded-2xl border border-zinc-800/40 bg-zinc-900/40 p-5 animate-pulse">
              <div className="h-3 bg-zinc-800 rounded w-1/2 mb-4" />
              <div className="h-12 bg-zinc-800 rounded w-1/3" />
            </div>
          ) : (
            <ActiveNowPanel
              activeNow={stats?.active_now ?? 0}
              zoneColor={color}
              totalProjects={stats?.total_projects ?? 0}
            />
          )}

          {/* 2. Top forked this week */}
          {loading ? (
            <div className="rounded-2xl border border-zinc-800/40 bg-zinc-900/40 p-5 animate-pulse">
              <div className="h-3 bg-zinc-800 rounded w-2/3 mb-4" />
              <div className="flex gap-3">
                <div className="w-14 h-14 bg-zinc-800 rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-3 bg-zinc-800 rounded w-3/4" />
                  <div className="h-2.5 bg-zinc-800 rounded w-1/2" />
                </div>
              </div>
            </div>
          ) : (
            <TopForkedPanel
              topFork={stats?.top_forked_week?.[0] ?? null}
              zoneColor={color}
            />
          )}

          {/* 3. Zone affinity */}
          {loading ? (
            <div className="rounded-2xl border border-zinc-800/40 bg-zinc-900/40 p-5 animate-pulse sm:col-span-2 lg:col-span-1">
              <div className="h-3 bg-zinc-800 rounded w-1/2 mb-4" />
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i}>
                    <div className="h-2.5 bg-zinc-800 rounded w-3/4 mb-1" />
                    <div className="h-1 bg-zinc-800 rounded" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="sm:col-span-2 lg:col-span-1">
              <ZoneAffinityPanel
                incomingZones={stats?.incoming_zones ?? []}
                outgoingZones={stats?.outgoing_zones ?? []}
                currentZoneId={zoneId}
                zoneColor={color}
              />
            </div>
          )}
        </div>

        {/* ── RECENT PROJECTS ─────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Recent Projects</h2>
            <button
              onClick={() => navigate(`/explore?zone=${zoneId}`)}
              className="flex items-center gap-1 text-[12px] font-semibold text-zinc-400 hover:text-white transition-colors"
            >
              See all
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : stats?.recent_projects?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {stats.recent_projects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  zoneColor={color}
                  onClick={() => navigate(`/dashboard?project=${project.id}`)}
                />
              ))}
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center py-16 rounded-2xl border border-dashed"
              style={{ borderColor: `${color}25` }}
            >
              <ZoneIcon className="w-10 h-10 mb-3 opacity-20" style={{ color }} />
              <p className="text-zinc-500 text-sm mb-1">No projects yet in this zone.</p>
              <p className="text-zinc-700 text-xs mb-4">Be the first to create one.</p>
              <button
                onClick={() => navigate('/dashboard?create=true')}
                className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: `${color}18`,
                  border: `1px solid ${color}30`,
                  color,
                }}
              >
                Create a Project
              </button>
            </div>
          )}
        </div>

        {/* ── Top forks list (additional context below grid) ──────────── */}
        {!loading && stats?.top_forked_week?.length > 1 && (
          <div className="mt-10">
            <h2 className="text-lg font-bold text-white mb-4">
              Most Forked This Week
            </h2>
            <div className="space-y-2">
              {stats.top_forked_week.map((project, idx) => (
                <button
                  key={project.id}
                  onClick={() => navigate(`/dashboard?project=${project.id}`)}
                  className="group w-full flex items-center gap-3 p-3 rounded-2xl border border-zinc-800/50
                             hover:border-zinc-700/60 bg-zinc-900/30 hover:bg-zinc-900/60 transition-all active:scale-[0.99] text-left"
                >
                  {/* Rank */}
                  <span
                    className="text-2xl font-black tabular-nums w-8 text-center flex-shrink-0"
                    style={{ color: idx === 0 ? color : '#3f3f46', opacity: idx === 0 ? 1 : 0.5 }}
                  >
                    {idx + 1}
                  </span>

                  {/* Thumb */}
                  <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 border border-zinc-800">
                    {project.image_url ? (
                      <img src={project.image_url} alt={project.name} className="w-full h-full object-cover" />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-lg font-black select-none"
                        style={{ background: `linear-gradient(135deg, ${color}15, #18181b)`, color, opacity: 0.4 }}
                      >
                        {project.name?.[0]}
                      </div>
                    )}
                  </div>

                  {/* Name + author */}
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-white truncate group-hover:text-purple-300 transition-colors">
                      {project.name}
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate">
                      @{project.author?.username}
                    </p>
                  </div>

                  {/* Forks */}
                  <span
                    className="flex items-center gap-1 text-[12px] font-bold flex-shrink-0"
                    style={{ color }}
                  >
                    <GitFork className="w-3.5 h-3.5" />
                    {project.fork_count}
                  </span>

                  <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}